import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  Tooltip
} from '@mui/material';
import { Edit, Add, Delete, Refresh, Check, Close } from '@mui/icons-material';
import api from '../../../services/api/client';

const CATEGORIES = [
  { value: 'scan', label: 'Scan' },
  { value: 'leak', label: 'Fuite de donnees' },
  { value: 'audit', label: 'Audit' },
  { value: 'report', label: 'Rapport' }
];

export default function CostsManagement({ onUpdate }) {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [newCost, setNewCost] = useState({
    action_type: '',
    action_name: '',
    credits_required: 1,
    description: '',
    category: 'scan',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const fetchCosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/credits/costs?include_inactive=true');
      setCosts(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement des coûts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  const handleEdit = (cost) => {
    setEditingCost({ ...cost });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingCost) return;

    try {
      setSaving(true);
      await api.patch(`/admin/credits/costs/${editingCost.id}`, {
        action_name: editingCost.action_name,
        credits_required: parseInt(editingCost.credits_required),
        description: editingCost.description,
        is_active: editingCost.is_active
      });
      setEditDialog(false);
      setEditingCost(null);
      fetchCosts();
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/admin/credits/costs', {
        ...newCost,
        credits_required: parseInt(newCost.credits_required)
      });
      setCreateDialog(false);
      setNewCost({
        action_type: '',
        action_name: '',
        credits_required: 1,
        description: '',
        category: 'scan',
        is_active: true
      });
      fetchCosts();
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cost) => {
    if (!window.confirm(`Desactiver l'action "${cost.action_name}" ?`)) return;

    try {
      await api.delete(`/admin/credits/costs/${cost.id}`);
      fetchCosts();
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'scan': return 'primary';
      case 'leak': return 'error';
      case 'audit': return 'warning';
      case 'report': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Coûts des Actions ({costs.length})
        </Typography>
        <Box display="flex" gap={1}>
          <Button startIcon={<Add />} variant="contained" onClick={() => setCreateDialog(true)}>
            Nouvelle Action
          </Button>
          <Button startIcon={<Refresh />} onClick={fetchCosts}>
            Actualiser
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Type Action</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell align="right">Credits</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {costs.map((cost) => (
              <TableRow key={cost.id} hover>
                <TableCell>
                  <code>{cost.action_type}</code>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="medium">{cost.action_name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={CATEGORIES.find(c => c.value === cost.category)?.label || cost.category}
                    color={getCategoryColor(cost.category)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="primary">
                    {cost.credits_required}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                    {cost.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {cost.is_active ? (
                    <Chip label="Actif" color="success" size="small" />
                  ) : (
                    <Chip label="Inactif" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => handleEdit(cost)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Desactiver">
                    <IconButton size="small" color="error" onClick={() => handleDelete(cost)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le Coût d'Action</DialogTitle>
        <DialogContent>
          {editingCost && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Type Action"
                value={editingCost.action_type || ''}
                disabled
                fullWidth
              />
              <TextField
                label="Nom"
                value={editingCost.action_name || ''}
                onChange={(e) => setEditingCost({ ...editingCost, action_name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Credits requis"
                type="number"
                value={editingCost.credits_required || 1}
                onChange={(e) => setEditingCost({ ...editingCost, credits_required: e.target.value })}
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Description"
                value={editingCost.description || ''}
                onChange={(e) => setEditingCost({ ...editingCost, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editingCost.is_active || false}
                    onChange={(e) => setEditingCost({ ...editingCost, is_active: e.target.checked })}
                  />
                }
                label="Action active"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} startIcon={<Close />}>
            Annuler
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Check />}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvelle Action</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Type Action (ex: scan_ldap)"
              value={newCost.action_type}
              onChange={(e) => setNewCost({ ...newCost, action_type: e.target.value })}
              fullWidth
              required
              helperText="Identifiant unique de l'action"
            />
            <TextField
              label="Nom affiche"
              value={newCost.action_name}
              onChange={(e) => setNewCost({ ...newCost, action_name: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Catégorie</InputLabel>
              <Select
                value={newCost.category}
                label="Catégorie"
                onChange={(e) => setNewCost({ ...newCost, category: e.target.value })}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Credits requis"
              type="number"
              value={newCost.credits_required}
              onChange={(e) => setNewCost({ ...newCost, credits_required: e.target.value })}
              fullWidth
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Description"
              value={newCost.description}
              onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newCost.is_active}
                  onChange={(e) => setNewCost({ ...newCost, is_active: e.target.checked })}
                />
              }
              label="Action active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} startIcon={<Close />}>
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={saving || !newCost.action_type || !newCost.action_name}
            startIcon={saving ? <CircularProgress size={20} /> : <Add />}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
